import {Button, Card, CardContent, CardHeader, colors, Grid, TextField, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import {RoomParty, RoomPartyMember} from '../../../graphql/groups.datatypes';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import updateRoomPartyMember, {UpdateRoomPartyMemberInput} from '../RoomPartyMemberEditModal/updateRoomPartyMember.api';
import {useSnackbar} from 'notistack';
import anyLoading from '../../../utils/anyLoading';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import clsx from 'clsx';
import getRoomPartiesUnderMaxOccupancy from './getRoomPartiesUnderMaxOccupancy';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
}));

const RoomPartyMemberChangeRoom = (props: {
    roomParty: RoomParty,
    roomPartyMember: RoomPartyMember,
    setSelectedRoomGuest: (member: RoomPartyMember | null) => void,
    setSelectedModal: (modelSelection: string) => void;
    className?: string,
}) => {
    const {roomPartyMember, className, setSelectedRoomGuest, setSelectedModal} = props;
    const classes = useStyles();
    const [sendUpdateRoomPartyMember, updateData] = updateRoomPartyMember();
    const {enqueueSnackbar} = useSnackbar();
    const [formState, setFormState] = useState({
        id: roomPartyMember.id,
        room_party_id: -1
    });
    const groupId = props.roomParty!.booking!.group_id!;
    const roomPartyId = props.roomParty!.id!;
    const roomPartiesData = getRoomPartiesUnderMaxOccupancy(groupId, roomPartyId)
    const loadingData = anyLoading(updateData, roomPartiesData);

    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            name: false,
            description: false,
        }
    });

    let roomPartiesCount = 0;
    if (!roomPartiesData.loading){
        roomPartiesCount = roomPartiesData!.data!.roomPartiesUnderMaxOccupancy!.length;
    }
    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        room_party_id: {
            numericality: {
                onlyInteger: true,
                greaterThan: 0,
                message: 'is required'
            }
        },
    };
    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        const sanitisedData: UpdateRoomPartyMemberInput = {
            data: {
                id: formState.id,
                room_party_id: formState.room_party_id!,
            }
        };
        sendUpdateRoomPartyMember({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Room Party Member Updated.', {variant: 'success'});
                setSelectedRoomGuest(null);
                setSelectedModal('');
            })
            .catch(() => {
                enqueueSnackbar('Room Party Member Update Failed!', {variant: 'error'});
            });
    };

    const getDisplayName = (members: any):RoomPartyMember => {
        const primaryMember:RoomPartyMember | undefined = primaryRoomMember(members);
        return primaryMember ? primaryMember : members[0]
    }

    const primaryRoomMember = (members: RoomPartyMember[]):RoomPartyMember | undefined => {
        return members.find((member: RoomPartyMember) => member.primary_occupant);
    }

    return (
        <Card className={clsx([className, classes.container])}>
            <LoadingOverlay loading={loadingData.loading}>
                <CardHeader title="Move Room Party Member"/>
                <CardContent className={classes.card}>
                    <Grid container spacing={3}>
                        {!loadingData.loading && roomPartiesCount === 0 &&
                            <Grid item xs={12}>
                                <Typography variant={'body1'} color={'error'}>
                                    No Room Parties are currently available
                                </Typography>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <TextField
                                {...hasError('room_party_id')}
                                fullWidth
                                label="Available Room Parties"
                                name="room_party_id"
                                onChange={handleFieldChange}
                                select
                                // eslint-disable-next-line react/jsx-sort-props
                                SelectProps={{native: true}}
                                value={formState.room_party_id}
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                disabled={roomPartiesCount === 0}
                            >
                                <option value={-1}>please select</option>
                                {roomPartiesData.data &&
                                    roomPartiesData.data.roomPartiesUnderMaxOccupancy &&
                                    roomPartiesData.data.roomPartiesUnderMaxOccupancy.map((roomParty) => {
                                        const displayName = getDisplayName(roomParty.members);
                                        return (
                                            <option key={roomParty.id} value={roomParty.id}>
                                                {displayName && displayName.groupMember && (
                                                    `${displayName.groupMember.first_name} ${displayName.groupMember.last_name}`
                                                )}
                                            </option>
                                        )
                                    })}
                            </TextField>
                            { roomPartyMember.hasVaryingDates &&
                                <Typography variant={'body1'} color={'primary'} style={{marginTop: '1rem'}}>
                                    This member has dates that vary from the group's base nights
                                </Typography>
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                className={classes.saveButton}
                                onClick={onSaveClick}
                                variant="contained"
                                disabled={!errors.isValid || formState.room_party_id === -1}
                                >Save</Button>
                            <Button
                                variant={'outlined'}
                                onClick={
                                    () => {
                                        setSelectedRoomGuest(null)
                                        setSelectedModal('')
                                    }
                                }>Cancel</Button>
                        </Grid>

                    </Grid>
                </CardContent>
            </LoadingOverlay>
        </Card>
    );
};

export default RoomPartyMemberChangeRoom;
